import { array, option, ord } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { forwardRef, useMemo } from 'react';
import { connect } from 'react-redux';
import {
    OccupancyState,
    ResourceOccupancyState,
    ResourceOccupancyStateResult,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { getEnterpriseProfileId } from '@mews-commander/legacy-entities/src/ducks';
import { getServiceEnterpriseId } from '@mews-commander/legacy-entities/src/ducks/services';
import { getResourceFeaturesByResourceId } from '@mews-commander/legacy-entities/src/selectors/resourceFeatures';
import {
    getResourceNameWithCategoryDetail,
    getResourceSearchValue,
} from '@mews-commander/legacy-entities/src/selectors/resources';
import { withAutoFocusingFirstFocusableElement } from '@mews-commander/legacy-form';
import { ResourceFeature } from '@mews-commander/resources';
import { ApplicationState } from '@mews-commander/types';
import { Select } from '@mews-commander/ui';
import { useCommanderSelector } from '@mews-commander/utils';
import { Container, DropdownItem, Labeled, Stack, Valued } from '@optimus-web/core';
import ResourceStateTag from '../../commander-ui/connected/ResourceStateTag';
import ResourceOccupancyStateTag from '../../commander-ui/ResourceOccupancyStateTag';
import { ResourceFeatureList } from './ResourceFeatureList';
import { getResourceOccupancyStateOrd } from './selectors';

interface OwnProps {
    label?: React.ReactNode;
    inline?: boolean;
    startUtc: string;
    endUtc: string;
    serviceId: string;
    selectedResourceId?: string | null;
    onChange?: (resourceId: string | null) => void;
    onBlur?: () => void;
    required?: boolean;
    resourceCategoryIds?: string[];
    excludedResourceIds?: string[];
    showHousekeepingStateTags?: boolean;
    dataTestId?: string;
}

interface StateProps {
    getResourceDetail: (resourceId: string) => string;
    getSearchValue: (resourceId: string, occupancyState: OccupancyState) => string;
    getFeatures: (resourceId: string) => ResourceFeature[];
    resourceOccupancyStateOrd: ord.Ord<ResourceOccupancyState>;
}

type ResourceSelectProps = OwnProps & StateProps;

interface GetMenuItemsProps
    extends Pick<
        ResourceSelectProps,
        | 'resourceCategoryIds'
        | 'excludedResourceIds'
        | 'selectedResourceId'
        | 'getResourceDetail'
        | 'getSearchValue'
        | 'getFeatures'
        | 'showHousekeepingStateTags'
    > {
    occupancyStates: ResourceOccupancyStateResult['resourceCategoryOccupancyStates'];
    resourceOccupancyStateOrd: ord.Ord<ResourceOccupancyState>;
}

const getMenuItems = ({
    occupancyStates,
    resourceCategoryIds,
    excludedResourceIds,
    selectedResourceId,
    showHousekeepingStateTags,
    resourceOccupancyStateOrd,
    getResourceDetail,
    getSearchValue,
    getFeatures,
}: GetMenuItemsProps) =>
    occupancyStates
        ? pipe(
              occupancyStates,
              array.filterMap(({ resourceCategoryId, resourceOccupancyStates }) => {
                  const resourceOccupancyStatesOption = option.fromNullable(resourceOccupancyStates);

                  if (resourceCategoryIds && array.isNonEmpty(resourceCategoryIds)) {
                      return resourceCategoryIds.includes(resourceCategoryId)
                          ? resourceOccupancyStatesOption
                          : option.none;
                  }

                  return resourceOccupancyStatesOption;
              }),
              array.flatten,
              array.sort(resourceOccupancyStateOrd),
              array.filterMap(({ resourceId, occupancyState }) =>
                  excludedResourceIds?.includes(resourceId) ||
                  (occupancyState === OccupancyState.ReservedLocked && resourceId !== selectedResourceId)
                      ? option.none
                      : option.some<React.ReactElement<Valued>>(
                            <DropdownItem
                                key={resourceId}
                                value={resourceId}
                                label={
                                    <Stack distribution="space-between">
                                        <Container padding={['0', '100', '0', '0']}>
                                            {getResourceDetail(resourceId)}
                                        </Container>
                                        <Stack spacing="100" inline>
                                            {showHousekeepingStateTags &&
                                            occupancyState !== OccupancyState.OutOfOrder ? (
                                                <ResourceStateTag resourceId={resourceId} withTooltip={false} />
                                            ) : null}
                                            <ResourceOccupancyStateTag occupancyState={occupancyState} />
                                        </Stack>
                                    </Stack>
                                }
                                description={
                                    getFeatures(resourceId).length > 0 ? (
                                        <ResourceFeatureList features={getFeatures(resourceId)} />
                                    ) : null
                                }
                                searchValue={getSearchValue(resourceId, occupancyState)}
                            />
                        )
              )
          )
        : undefined;

const ResourceSelectComponent = forwardRef<HTMLDivElement, ResourceSelectProps>(
    (
        {
            startUtc,
            endUtc,
            selectedResourceId,
            onChange,
            serviceId,
            resourceCategoryIds,
            getResourceDetail,
            getSearchValue,
            getFeatures,
            excludedResourceIds,
            dataTestId,
            showHousekeepingStateTags = true,
            resourceOccupancyStateOrd,
            ...other
        },
        ref
    ) => {
        const enterpriseId = useCommanderSelector((state) => getServiceEnterpriseId(state, { id: serviceId }));
        const profileId = useCommanderSelector((state) => getEnterpriseProfileId(state, { enterpriseId }));

        const { data, isFetching } = useRepositoryQuery('resources', 'getOccupancyState', {
            serviceId,
            // @ts-expect-error
            profileId,
            startUtc,
            endUtc,
        });

        const menuItems = useMemo(
            () =>
                getMenuItems({
                    occupancyStates: data?.resourceCategoryOccupancyStates,
                    resourceCategoryIds,
                    excludedResourceIds,
                    selectedResourceId,
                    showHousekeepingStateTags,
                    resourceOccupancyStateOrd,
                    getResourceDetail,
                    getSearchValue,
                    getFeatures,
                }),
            [
                data?.resourceCategoryOccupancyStates,
                resourceCategoryIds,
                excludedResourceIds,
                selectedResourceId,
                showHousekeepingStateTags,
                resourceOccupancyStateOrd,
                getResourceDetail,
                getSearchValue,
                getFeatures,
            ]
        );

        return (
            <div ref={ref}>
                <Select
                    name={dataTestId}
                    selected={selectedResourceId}
                    onChange={(id: string | null) => {
                        if (onChange && id !== selectedResourceId) {
                            onChange(id || null);
                        }
                    }}
                    showEmptyValue
                    // @ts-expect-error $TSFixMeError: Type '{ children: Element[] | null; label?: ReactNode; inline?: boolean | undefined; onBlur?: (() => void) | undefined; ...
                    searchValueMapper={({ props: { searchValue } }: React.ReactElement<Labeled>) => searchValue}
                    loader={isFetching}
                    {...other}
                >
                    {menuItems}
                </Select>
            </div>
        );
    }
);

const inject = connect<StateProps, {}, OwnProps, ApplicationState>((state, { serviceId }) => ({
    getResourceDetail: (resourceId) => getResourceNameWithCategoryDetail(state, { serviceId, resourceId }),
    getSearchValue: (resourceId, occupancyState) =>
        getResourceSearchValue(state, { serviceId, resourceId, occupancyState }),
    resourceOccupancyStateOrd: getResourceOccupancyStateOrd(state),
    getFeatures: (resourceId) => getResourceFeaturesByResourceId(state, { resourceId }),
}));

export const ResourceSelect = pipe(ResourceSelectComponent, inject);

export const AutoFocusedResourceSelect = pipe(ResourceSelectComponent, withAutoFocusingFirstFocusableElement(), inject);
